import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import {
  FiBarChart2,
  FiTrendingUp,
  FiPieChart,
  FiActivity,
  FiTable,
  FiDownload,
  FiCopy,
  FiCheck
} from 'react-icons/fi';

const CURATED_PALETTE = [
  '#0284c7', // Sky Blue
  '#10b981', // Emerald
  '#8b5cf6', // Violet
  '#f59e0b', // Amber
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#6366f1', // Indigo
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#64748b'  // Slate
];

const formatValue = (val, format = 'number') => {
  if (val === null || val === undefined || isNaN(Number(val))) return '-';
  const num = Number(val);
  if (format === 'currency') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  }
  if (format === 'percent') {
    return `${num.toFixed(1)}%`;
  }
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 2
  }).format(num);
};

const formatShortAxis = (val, format = 'number') => {
  if (val === null || val === undefined || isNaN(Number(val))) return '';
  const num = Number(val);
  if (format === 'currency') {
    if (Math.abs(num) >= 1000000) return `R$ ${(num / 1000000).toFixed(1)}M`;
    if (Math.abs(num) >= 1000) return `R$ ${(num / 1000).toFixed(0)}k`;
    return `R$ ${num.toFixed(0)}`;
  }
  if (format === 'percent') {
    return `${num.toFixed(0)}%`;
  }
  if (Math.abs(num) >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(0)}k`;
  return String(num);
};

// Custom tooltip with modern styling
const CustomChartTooltip = ({ active, payload, label, format = 'number', totalValue }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(8px)',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '8px 12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      fontSize: '0.75rem',
      minWidth: 140,
      zIndex: 100
    }}>
      {label && (
        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 6, borderBottom: '1px solid #f1f5f9', paddingBottom: 4 }}>
          {label}
        </div>
      )}
      {payload.map((entry, idx) => {
        const val = entry.value;
        const pct = totalValue && totalValue > 0 ? ((val / totalValue) * 100).toFixed(1) : null;
        return (
          <div key={`item-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '3px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color || entry.fill || '#0284c7', display: 'inline-block' }} />
              <span style={{ color: '#64748b' }}>{entry.name || entry.dataKey}:</span>
            </div>
            <span style={{ fontWeight: 600, color: '#0f172a' }}>
              {formatValue(val, format)}
              {pct !== null && <span style={{ color: '#94a3b8', fontSize: '0.7rem', marginLeft: 4 }}>({pct}%)</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

function AIChartCard({ config }) {
  const [copied, setCopied] = useState(false);
  const [showTable, setShowTable] = useState(false);

  // Parse and normalize config
  const rawType = (config?.type || config?.chartType || 'bar').toLowerCase();
  const [activeType, setActiveType] = useState(() => {
    if (['pie', 'doughnut', 'donut'].includes(rawType)) return 'pie';
    if (['line', 'trend'].includes(rawType)) return 'line';
    if (['area'].includes(rawType)) return 'area';
    return 'bar';
  });

  const title = config?.title || 'Gráfico de Dados';
  const subtitle = config?.subtitle || null;
  const rawData = useMemo(() => Array.isArray(config?.data) ? config.data : [], [config]);

  // Auto-detect format if not explicitly provided
  const format = useMemo(() => {
    if (config?.format) return config.format;
    const titleLower = (title + ' ' + (subtitle || '')).toLowerCase();
    if (titleLower.includes('faturamento') || titleLower.includes('venda') || titleLower.includes('valor') ||
        titleLower.includes('meta') || titleLower.includes('receita') || titleLower.includes('ticket') ||
        titleLower.includes('r$') || titleLower.includes('preço')) {
      return 'currency';
    }
    if (titleLower.includes('%') || titleLower.includes('atingimento') || titleLower.includes('percentual') || titleLower.includes('taxa')) {
      return 'percent';
    }
    return 'number';
  }, [config, title, subtitle]);

  // Determine X-axis and series
  const { xAxisKey, series, isPieCompatible, totalPieValue } = useMemo(() => {
    if (rawData.length === 0) {
      return { xAxisKey: 'name', series: [], isPieCompatible: false, totalPieValue: 0 };
    }

    const firstRow = rawData[0];
    const keys = Object.keys(firstRow);

    // Auto-detect X-axis key
    let xKey = config?.xAxisKey || config?.nameKey || null;
    if (!xKey || !keys.includes(xKey)) {
      xKey = keys.find(k => typeof firstRow[k] === 'string') || keys[0];
    }

    // Auto-detect series
    let detectedSeries = [];
    if (Array.isArray(config?.series) && config.series.length > 0) {
      detectedSeries = config.series.map((s, idx) => ({
        key: s.key,
        label: s.label || s.name || s.key,
        color: s.color || CURATED_PALETTE[idx % CURATED_PALETTE.length]
      }));
    } else if (config?.valueKey) {
      detectedSeries = [{
        key: config.valueKey,
        label: config.valueLabel || config.valueKey,
        color: CURATED_PALETTE[0]
      }];
    } else {
      // Find all numeric columns excluding the X axis
      const numericKeys = keys.filter(k => k !== xKey && !isNaN(Number(firstRow[k])));
      detectedSeries = numericKeys.map((k, idx) => ({
        key: k,
        label: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        color: CURATED_PALETTE[idx % CURATED_PALETTE.length]
      }));
      if (detectedSeries.length === 0 && keys.length > 1) {
        detectedSeries = [{ key: keys[1], label: keys[1], color: CURATED_PALETTE[0] }];
      }
    }

    // Total for Pie
    const pieValKey = detectedSeries[0]?.key || 'value';
    const total = rawData.reduce((acc, item) => acc + (Number(item[pieValKey]) || 0), 0);

    return {
      xAxisKey: xKey,
      series: detectedSeries,
      isPieCompatible: detectedSeries.length === 1,
      totalPieValue: total
    };
  }, [rawData, config]);

  // Export CSV handler
  const handleExportCsv = () => {
    if (rawData.length === 0) return;
    const headers = [xAxisKey, ...series.map(s => s.label)];
    const rows = rawData.map(row => {
      return [
        `"${row[xAxisKey] ?? ''}"`,
        ...series.map(s => row[s.key] ?? '')
      ].join(';');
    });
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Copy data handler
  const handleCopyData = () => {
    navigator.clipboard.writeText(JSON.stringify(rawData, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (rawData.length === 0) {
    return (
      <div style={{
        padding: '12px 16px',
        background: '#f8fafc',
        border: '1px dashed #cbd5e1',
        borderRadius: 8,
        fontSize: '0.75rem',
        color: '#64748b',
        margin: '8px 0'
      }}>
        Nenhum dado numérico disponível para exibição gráfica.
      </div>
    );
  }

  // Formatting for X axis rotation if labels are many or long
  const shouldRotateX = rawData.length > 5 || rawData.some(d => String(d[xAxisKey] || '').length > 8);

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      padding: '12px 14px',
      margin: '10px 0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        borderBottom: '1px solid #f1f5f9',
        paddingBottom: 8,
        marginBottom: 10
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 6,
              background: '#eff6ff',
              color: '#0284c7'
            }}>
              {activeType === 'pie' ? <FiPieChart size={13} /> : activeType === 'line' ? <FiTrendingUp size={13} /> : <FiBarChart2 size={13} />}
            </span>
            <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#0f172a' }}>
              {title}
            </span>
            <span style={{
              fontSize: '0.62rem',
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 4,
              background: '#f1f5f9',
              color: '#475569',
              textTransform: 'uppercase'
            }}>
              {activeType === 'pie' ? (rawType.includes('dough') ? 'Rosca' : 'Pizza') : activeType === 'line' ? 'Linha' : activeType === 'area' ? 'Área' : 'Barras'}
            </span>
          </div>
          {subtitle && (
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2, paddingLeft: 30 }}>
              {subtitle}
            </div>
          )}
        </div>

        {/* Action buttons & type switchers */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {/* Type toggles */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 6, padding: 2, gap: 1 }}>
            <button
              onClick={() => { setActiveType('bar'); setShowTable(false); }}
              title="Visualização em Barras"
              style={{
                background: activeType === 'bar' && !showTable ? '#ffffff' : 'none',
                color: activeType === 'bar' && !showTable ? '#0284c7' : '#64748b',
                border: 'none',
                borderRadius: 4,
                padding: '3px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                boxShadow: activeType === 'bar' && !showTable ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
              }}
            >
              <FiBarChart2 size={12} />
            </button>
            <button
              onClick={() => { setActiveType('line'); setShowTable(false); }}
              title="Visualização em Linha"
              style={{
                background: activeType === 'line' && !showTable ? '#ffffff' : 'none',
                color: activeType === 'line' && !showTable ? '#0284c7' : '#64748b',
                border: 'none',
                borderRadius: 4,
                padding: '3px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                boxShadow: activeType === 'line' && !showTable ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
              }}
            >
              <FiTrendingUp size={12} />
            </button>
            <button
              onClick={() => { setActiveType('area'); setShowTable(false); }}
              title="Visualização em Área"
              style={{
                background: activeType === 'area' && !showTable ? '#ffffff' : 'none',
                color: activeType === 'area' && !showTable ? '#0284c7' : '#64748b',
                border: 'none',
                borderRadius: 4,
                padding: '3px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                boxShadow: activeType === 'area' && !showTable ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
              }}
            >
              <FiActivity size={12} />
            </button>
            {isPieCompatible && (
              <button
                onClick={() => { setActiveType('pie'); setShowTable(false); }}
                title="Visualização em Pizza / Distribuição"
                style={{
                  background: activeType === 'pie' && !showTable ? '#ffffff' : 'none',
                  color: activeType === 'pie' && !showTable ? '#0284c7' : '#64748b',
                  border: 'none',
                  borderRadius: 4,
                  padding: '3px 6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: activeType === 'pie' && !showTable ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
                }}
              >
                <FiPieChart size={12} />
              </button>
            )}
          </div>

          {/* Table view toggle */}
          <button
            onClick={() => setShowTable(prev => !prev)}
            title={showTable ? 'Ver Gráfico' : 'Ver Tabela'}
            style={{
              background: showTable ? '#eff6ff' : '#f8fafc',
              border: '1px solid #e2e8f0',
              color: showTable ? '#0284c7' : '#64748b',
              borderRadius: 6,
              padding: '4px 6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FiTable size={12} />
          </button>

          {/* Export CSV */}
          <button
            onClick={handleExportCsv}
            title="Baixar dados em CSV"
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#64748b',
              borderRadius: 6,
              padding: '4px 6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FiDownload size={12} />
          </button>

          {/* Copy data */}
          <button
            onClick={handleCopyData}
            title="Copiar dados JSON"
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: copied ? '#10b981' : '#64748b',
              borderRadius: 6,
              padding: '4px 6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
          </button>
        </div>
      </div>

      {/* Chart or Table view */}
      {showTable ? (
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <th style={{ padding: '6px 10px', fontWeight: 600 }}>{xAxisKey}</th>
                {series.map(s => (
                  <th key={s.key} style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right' }}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rawData.map((row, rIdx) => (
                <tr key={rIdx} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '6px 10px', color: '#334155', fontWeight: 500 }}>
                    {String(row[xAxisKey] ?? '')}
                  </td>
                  {series.map(s => (
                    <td key={s.key} style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                      {formatValue(row[s.key], format)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ width: '100%', height: 260, position: 'relative' }}>
          <ResponsiveContainer width="100%" height={260}>
            {activeType === 'pie' ? (
              <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <Tooltip
                  content={<CustomChartTooltip format={format} totalValue={totalPieValue} />}
                />
                <Legend
                  verticalAlign="bottom"
                  height={32}
                  wrapperStyle={{ fontSize: '0.72rem', color: '#475569' }}
                />
                <Pie
                  data={rawData}
                  dataKey={series[0]?.key || 'value'}
                  nameKey={xAxisKey}
                  cx="50%"
                  cy="45%"
                  outerRadius={75}
                  innerRadius={rawType.includes('dough') ? 45 : 0}
                  paddingAngle={2}
                  isAnimationActive={true}
                >
                  {rawData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CURATED_PALETTE[index % CURATED_PALETTE.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
            ) : activeType === 'line' ? (
              <LineChart data={rawData} margin={{ top: 10, right: 15, left: 0, bottom: shouldRotateX ? 35 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey={xAxisKey}
                  stroke="#94a3b8"
                  fontSize={10}
                  tickLine={false}
                  interval={0}
                  angle={shouldRotateX ? -25 : 0}
                  textAnchor={shouldRotateX ? 'end' : 'middle'}
                  height={shouldRotateX ? 45 : 25}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={v => formatShortAxis(v, format)}
                  width={45}
                />
                <Tooltip content={<CustomChartTooltip format={format} />} />
                <Legend
                  verticalAlign="top"
                  height={26}
                  wrapperStyle={{ fontSize: '0.72rem', color: '#475569', paddingBottom: 4 }}
                />
                {series.map(s => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: s.color, strokeWidth: 0 }}
                    activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2 }}
                    isAnimationActive={true}
                  />
                ))}
              </LineChart>
            ) : activeType === 'area' ? (
              <AreaChart data={rawData} margin={{ top: 10, right: 15, left: 0, bottom: shouldRotateX ? 35 : 5 }}>
                <defs>
                  {series.map((s, idx) => (
                    <linearGradient key={`grad-${s.key}`} id={`grad-${s.key}-${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0.0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey={xAxisKey}
                  stroke="#94a3b8"
                  fontSize={10}
                  tickLine={false}
                  interval={0}
                  angle={shouldRotateX ? -25 : 0}
                  textAnchor={shouldRotateX ? 'end' : 'middle'}
                  height={shouldRotateX ? 45 : 25}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={v => formatShortAxis(v, format)}
                  width={45}
                />
                <Tooltip content={<CustomChartTooltip format={format} />} />
                <Legend
                  verticalAlign="top"
                  height={26}
                  wrapperStyle={{ fontSize: '0.72rem', color: '#475569', paddingBottom: 4 }}
                />
                {series.map((s, idx) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={`url(#grad-${s.key}-${idx})`}
                    isAnimationActive={true}
                  />
                ))}
              </AreaChart>
            ) : (
              <BarChart data={rawData} margin={{ top: 10, right: 15, left: 0, bottom: shouldRotateX ? 35 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey={xAxisKey}
                  stroke="#94a3b8"
                  fontSize={10}
                  tickLine={false}
                  interval={0}
                  angle={shouldRotateX ? -25 : 0}
                  textAnchor={shouldRotateX ? 'end' : 'middle'}
                  height={shouldRotateX ? 45 : 25}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={v => formatShortAxis(v, format)}
                  width={45}
                />
                <Tooltip content={<CustomChartTooltip format={format} />} />
                <Legend
                  verticalAlign="top"
                  height={26}
                  wrapperStyle={{ fontSize: '0.72rem', color: '#475569', paddingBottom: 4 }}
                />
                {series.map(s => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    fill={s.color}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={45}
                    isAnimationActive={true}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default AIChartCard;
